import {
  EAuthenticationType,
  EDatasourceType,
  ICommonSettings,
  IDatasourceDto,
  IRestApiDatasourceBinding,
  REST_DATASOURCE_TYPES
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { PureComponent, ReactNode } from "react";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaEmptyState from "../../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigField } from "../config-pane-parts";
import { ConfigRestApiSettings, REST_API_SETTINGS_SEARCH_TERMS } from "./app-settings-config-rest-api";
import "./app-settings-config-rest-api-input.scss";

export interface IRestApiConnectionSetting {
  id: string | null;
  name: string;
  baseUrl?: string;
  authType?: EAuthenticationType;
  ClientIdKeyName?: string;
  ClientId?: string;
  ClientSecretKeyName?: string;
  ClientSecret?: string;
  BasicAuthUsername?: string;
  BasicAuthPassword?: string;
}

export interface IConfigRestApiInputState {
  showModal: boolean;
  editingRestApiSettingId: string;
}

export interface IConfigRestApiInputProps {
  parentConfigValue?: IRestApiDatasourceBinding[] | null;
  commonSettings: ICommonSettings;
  datasources: IDatasourceDto[];
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput: (formItemName: string[], value: string | IRestApiDatasourceBinding[] | null) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

const BINDINGS_PATH: string[] = ["apiConfig", "restApiDatasourceBindings"];
// `createCommonConfig` / `updateCommonConfig` / `deleteCommonConfig` in the container
// auto-write the affected setting's id into this form field after a save. With
// per-datasource bindings the user picks per row, so we route the auto-write to the
// legacy single-id field where it's a harmless no-op (codegen ignores it once bindings
// exist).
const COMMON_CONFIG_WRITE_PATH: string[] = ["apiConfig", "restApiConnectionSettingsId"];

const TYPE_BADGE: Record<number, string> = {
  [EDatasourceType.RestApi]: "REST",
  [EDatasourceType.RestApiWOSwagger]: "MANUAL",
  [EDatasourceType.OData]: "ODATA"
};

export class ConfigRestApiInput extends PureComponent<IConfigRestApiInputProps, IConfigRestApiInputState> {
  formInputName: string[] = BINDINGS_PATH;

  constructor(props: IConfigRestApiInputProps) {
    super(props);
    this.state = {
      showModal: false,
      editingRestApiSettingId: ""
    };
  }

  openModal = (): void => {
    this.setState({ showModal: true, editingRestApiSettingId: "" });
  };

  closeModal = (): void => {
    this.setState({ showModal: false });
  };

  setEditingRestApiSetting = (id: string): void => {
    this.setState({ editingRestApiSettingId: id, showModal: true });
  };

  getRestDatasources = (): IDatasourceDto[] => {
    const { datasources } = this.props;
    if (!datasources) return [];
    return datasources.filter((ds: IDatasourceDto) => REST_DATASOURCE_TYPES.has(ds.datasourceType as EDatasourceType));
  };

  handleBindingChange = (
    bindings: IRestApiDatasourceBinding[] | null,
    datasourceId: string,
    newSettingsId: string | null
  ): void => {
    const current: IRestApiDatasourceBinding[] = bindings ? [...bindings] : [];
    const idx: number = current.findIndex((b: IRestApiDatasourceBinding) => b.datasourceId === datasourceId);
    const nextEntry: IRestApiDatasourceBinding = {
      datasourceId,
      restApiConnectionSettingsId: newSettingsId
    };
    if (idx >= 0) {
      current[idx] = nextEntry;
    } else {
      current.push(nextEntry);
    }
    this.props.handleUpdateCustomFormInput(this.formInputName, current);
  };

  /** The datasource's name over the badge that says which kind of datasource it is. */
  renderLabel = (ds: IDatasourceDto): ReactNode => (
    <span className="kuika-rest-api-binding__label">
      <span className="kuika-rest-api-binding__name" title={ds.name}>
        {ds.name}
      </span>
      <KuikaBadge content={TYPE_BADGE[ds.datasourceType] ?? "REST"} color="grey" size="small" />
    </span>
  );

  renderRow = (
    ds: IDatasourceDto,
    bindings: IRestApiDatasourceBinding[] | null,
    options: IRestApiConnectionSetting[]
  ): ReactNode => {
    const binding: IRestApiDatasourceBinding | undefined = bindings?.find(
      (b: IRestApiDatasourceBinding) => b.datasourceId === ds.id
    );
    const currentId: string | null = binding?.restApiConnectionSettingsId ?? null;

    return (
      <ConfigField
        key={ds.id}
        // Findable, not openable: this row is not a `ConfigSettingSelect` and renders no
        // opener, so `searchTermsOpenDialog` stays off and a result lands on the row —
        // the row's own control is the way in.
        searchTerms={REST_API_SETTINGS_SEARCH_TERMS}
        label={this.renderLabel(ds)}
        tooltipContentKey="appsettings_restapi_connectionsettings"
        hint={this.renderHint(options)}
        warning={
          ds.isStaleRestApiClient && (
            <span data-testid={`appSettingsRestApiStale-${ds.name}`}>
              OUT OF DATE — the datasource changed since this was saved.
            </span>
          )
        }
      >
        {/* Not a `Form.Item`: the bindings are ONE array field, registered once below and
            written whole on every change, so each row's control is uncontrolled by antd
            and drives `handleBindingChange` itself. */}
        <span data-testid={`appSettingsRestApiRow-${ds.name}`}>
          <KuikaSelectbox
            size="md"
            data-testid={`appSettingsRestApiDropdown-${ds.name}`}
            placeholder="Default"
            value={currentId ?? undefined}
            options={this.getOptions(options)}
            onChange={(value: unknown) => {
              const next: string | null = typeof value === "string" && value.length > 0 ? value : null;
              this.handleBindingChange(bindings, ds.id, next);
            }}
          />
        </span>
      </ConfigField>
    );
  };

  /** The same "ADD NEW · N saved" line the shared picker draws. */
  renderHint = (options: IRestApiConnectionSetting[]): ReactNode => {
    const saved: number = options.filter((option: IRestApiConnectionSetting): boolean => option.id !== null).length;
    return (
      <>
        <button
          type="button"
          className="kuika-config-setting__add-new"
          data-testid="appSettingsRestApiDropdownAddNew"
          onClick={this.openModal}
        >
          ADD NEW
        </button>
        <span className="kuika-config-setting__saved-count">
          {" · "}
          {saved} saved
        </span>
      </>
    );
  };

  /**
   * Same option shape as the shared picker's, so the edit affordance behaves the same, and the
   * "Default" entry carries its null id: the kit takes null for the option that means "no value"
   * and hands it back through `onChange`, which this row already reads as "no saved setting".
   */
  getOptions = (options: IRestApiConnectionSetting[]): KuikaSelectboxOption[] =>
    options.map((option: IRestApiConnectionSetting): KuikaSelectboxOption => ({
      value: option.id ?? null,
      searchLabel: option.name,
      label: (
        <span className="kuika-config-setting__option">
          <span className="kuika-config-setting__option-name">{option.name}</span>
          {option.id && (
            <button
              type="button"
              className="kuika-config-setting__option-edit"
              aria-label={`Edit ${option.name}`}
              data-testid="appSettingsRestApiEditButton"
              onMouseDown={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                this.setEditingRestApiSetting(option.id as string);
              }}
            >
              <KuikaSvg name="settings" width={16} height={16} />
            </button>
          )}
        </span>
      )
    }));

  renderList = (form: FormInstance): ReactNode => {
    const { commonSettings } = this.props;
    const restApiSettings: IRestApiConnectionSetting[] | undefined = (
      commonSettings as unknown as { restApiConnectionSettings?: IRestApiConnectionSetting[] }
    ).restApiConnectionSettings;
    const options: IRestApiConnectionSetting[] = [{ id: null, name: "Default" }, ...(restApiSettings ?? [])];
    const restDatasources: IDatasourceDto[] = this.getRestDatasources();
    const rawBindings: unknown = form.getFieldValue(BINDINGS_PATH);
    const bindings: IRestApiDatasourceBinding[] | null = Array.isArray(rawBindings)
      ? (rawBindings as IRestApiDatasourceBinding[])
      : null;

    // Nothing to bind. It was a `ConfigField` whose label was the whole message, which put
    // "No REST/Swagger/OData datasource" in the 148px label gutter and the explanation
    // beside it — an empty state wearing a field's geometry, and it read as one that had
    // failed to load. It is a block of its own now.
    if (restDatasources.length === 0) {
      return (
        <KuikaEmptyState
          svgType="datasource-icons"
          svgName="swagger"
          customName="No REST, Swagger or OData datasource"
          description="Add one from the data panel, and it will appear here for a connection configuration."
        />
      );
    }

    return <>{restDatasources.map((ds: IDatasourceDto) => this.renderRow(ds, bindings, options))}</>;
  };

  render(): ReactNode {
    const { commonSettings, getSectionDescription } = this.props;

    return (
      <>
        <ConfigRestApiSettings
          formInputName={COMMON_CONFIG_WRITE_PATH}
          showModal={this.state.showModal}
          closeModal={this.closeModal}
          editingRestApiSettingId={this.state.editingRestApiSettingId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />

        {/* Hidden registration: ensures `apiConfig.restApiDatasourceBindings` is a tracked
            form field so antd persists it through onFinish. The Select inputs below are
            controlled via setFieldsValue, but without this registration the field can be
            dropped from the submitted form values. */}
        <Form.Item name={BINDINGS_PATH} hidden>
          <input type="hidden" />
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {(form: FormInstance): ReactNode => this.renderList(form)}
        </Form.Item>
      </>
    );
  }
}
